import { useSortable } from "@dnd-kit/sortable";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { BarsMenuIcon, EditIcon, TrashAltIcon } from "assets/icons";
import cn from "classnames";
import { DragDropChangeValue } from "components";
import { moveElementInArray } from "helpers";
import { useLanguage } from "hooks";
import { t } from "i18next";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  ItemsTableDragDrop,
  Modal,
  Status,
  TableItemAction,
  TableItemActionsControl,
  TableItemStatusControl
} from "ui";

import { useQuizUpdate } from "../../apis";
import { QUIZ_ITEM_STATUSES_LIST, QuizItemStatuses } from "../../constants";
import { QuizQuestionItemWithId, QuizTableProps } from "./QuizTable.types";

import styles from "./QuizTable.module.scss";

const QuizTable = ({
  quizList,
  correctAnswersNeededToPass,
  isDragDisabled,
  onEditItem
}: QuizTableProps) => {
  const quizListWithId = quizList.map((quiz, index) => ({
    ...quiz,
    id: String(index)
  }));

  const { currentLanguage } = useLanguage();
  const queryClient = useQueryClient();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);
  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);
  const [removingQuizItemIndex, setRemovingQuizItemIndex] = useState<
    number | null
  >(null);

  const quizUpdate = useQuizUpdate();

  const onChangeQuizItemPriority = (value: DragDropChangeValue) => {
    const { fromIndex, toIndex } = value;
    const updatedQuizList = moveElementInArray({
      array: quizList,
      fromIndex,
      toIndex
    });

    queryClient.setQueryData(["content-quiz"], {
      content: {
        correctAnswersNeededToPass,
        quizzes: updatedQuizList
      }
    });

    quizUpdate.mutate({
      correctAnswersNeededToPass,
      quizzes: updatedQuizList
    });
  };

  const onQuestionDelete = async () => {
    await quizUpdate.mutateAsync({
      correctAnswersNeededToPass:
        removingQuizItemIndex &&
        quizList[removingQuizItemIndex].status === QuizItemStatuses.Active
          ? correctAnswersNeededToPass - 1
          : correctAnswersNeededToPass,
      quizzes: quizList.filter((_, index) => index !== removingQuizItemIndex)
    });

    setRemovingQuizItemIndex(null);
  };

  const RowDragHandleCell = ({ rowId }: { rowId: string }) => {
    const { attributes, listeners } = useSortable({
      id: rowId
    });
    return (
      <div
        {...attributes}
        {...listeners}
        className={cn(styles.quizTable_grab, {
          [styles["quizTable_grab--disabled"]]: isDragDisabled
        })}
      >
        <BarsMenuIcon />
      </div>
    );
  };

  const statusesList: Status[] = QUIZ_ITEM_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: async () => {
      try {
        if (openedItemStatusesIndex !== null) {
          await quizUpdate.mutateAsync({
            correctAnswersNeededToPass:
              orStatus.value === QuizItemStatuses.Active
                ? correctAnswersNeededToPass + 1
                : correctAnswersNeededToPass - 1,
            quizzes: quizList.map((quiz, quizIndex) =>
              quizIndex === openedItemStatusesIndex
                ? { ...quiz, status: orStatus.value }
                : quiz
            )
          });

          toast.success(t("Quiz has been updated successfully"));

          setOpenedItemStatusesIndex(null);
        }
      } catch {
        // non-critical, ignore
      }
    }
  }));

  const columns: Array<
    ColumnDef<QuizQuestionItemWithId, QuizQuestionItemWithId[]>
  > = useMemo(
    () => [
      {
        id: "order",
        header: "",
        size: 4,
        minSize: 4,
        cell: ({ row }) => <RowDragHandleCell rowId={row.id} />
      },
      {
        id: "priority",
        header: "#",
        size: 3,
        minSize: 3,
        cell: ({ row }) => (
          <div className={styles.quizTable_boldText}>{row.index + 1}</div>
        )
      },
      {
        id: "question",
        header: t("Question"),
        size: 27,
        minSize: 27,
        cell: ({ row }) => (
          <div className={styles.quizTable_question}>
            <span className={styles.quizTable_boldText}>
              {row.original.question}
            </span>
          </div>
        )
      },
      {
        id: "question",
        header: t("Answers"),
        size: 27,
        minSize: 27,
        cell: ({ row }) => (
          <div className={styles.quizTable_question}>
            {row.original.answers.map((answer, index) => (
              <div
                key={index}
                className={cn({
                  [styles.quizTable_correctAnswer]: answer.isCorrect
                })}
              >
                {`${index + 1}. ${answer.answer}`}
              </div>
            ))}
          </div>
        )
      },

      {
        id: "isActive",
        accessorKey: "isActive",
        header: t("Status"),
        size: 8,
        minSize: 8,
        cell: ({ row }) => (
          <TableItemStatusControl
            rowIndex={row.index}
            activeStatusIndex={
              quizList[row.index].status === QuizItemStatuses.Active ? 0 : 1
            }
            openedItemStatusesIndex={openedItemStatusesIndex}
            setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
            statusesList={statusesList}
          />
        )
      },

      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 4,
        minSize: 4,
        cell: ({ row }) => {
          const actionsList: TableItemAction[] = [
            {
              label: "Edit",
              Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
              onClick: () => onEditItem(row.index)
            }
          ];

          if (quizList.length > 1) {
            actionsList.push({
              label: "Delete",
              Icon: <TrashAltIcon fill={"#ff0033"} width={18} height={18} />,
              labelColor: "#ff0033",
              onClick: () => setRemovingQuizItemIndex(row.index)
            });
          }

          return (
            <TableItemActionsControl
              actionsList={actionsList}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          );
        },
        enableSorting: false
      }
    ],
    [quizList, currentLanguage, openedItemOptionsIndex, openedItemStatusesIndex]
  );

  return (
    <>
      <ItemsTableDragDrop
        className={styles.quizTable}
        columns={columns}
        data={quizListWithId}
        isLoading={false}
        droppableId={"quizList"}
        onChange={onChangeQuizItemPriority}
        isDragDisabled={isDragDisabled}
      />

      <Modal
        type="delete"
        isOpen={removingQuizItemIndex !== null}
        title="Delete Question"
        isLoading={quizUpdate.isLoading}
        okText="Delete"
        onOk={onQuestionDelete}
        contentClassName={styles.deleteModal}
        onClose={() => setRemovingQuizItemIndex(null)}
      >
        <p className={styles.deleteModal_text}>
          {t("Are you sure you want to delete this question?")}
        </p>
      </Modal>
    </>
  );
};

export default QuizTable;
